import { useEffect, useState } from 'react';
import { Box, Breadcrumbs, CircularProgress, Typography } from '@mui/material';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import Button from 'shared/components/widgets/button';
import Input from 'shared/components/widgets/input';
import Autocomplete from 'shared/components/widgets/autocomplete';
import RouteLink from 'shared/components/widgets/route-link';
import InputContainer from 'shared/components/widgets/input-container';

import { isAdmin, isStaff } from '@utils/getRole';
import { ROUTES } from 'constants/routes';
import { accountInfoDetailService } from '@services/private/account-info/account-info-detail';
import { is200xRange } from '@utils/utilities';
import { Controller, useForm } from 'react-hook-form';
import { accountInfoUpdateService } from '@services/private/account-info/account-info-update';
import { useNavigate } from 'react-router-dom';
import { getItem, removeItem } from '@utils/localStorage';
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup';
import { validateEmail } from 'shared/helpers/validateEmail';
type AccountInfoData = any;

const InformationQuerySchema = yup.object({
  admName: yup.string().required('관리자 이름은 필수 항목입니다').min(3, '최소 3자 이상 입력해 주세요'),
  emailName: yup.string(),
  emailDomain: yup.string(),
  customDomain: yup.string(),
  newDomain: yup.string()
});

const InformationInquiry = () => {
  const navigate = useNavigate();
  const [isEditInfo, setIsEditInfo] = useState(false);
  const [isLoading, setLoading] = useState<boolean>(false)
  const [errorEmail, setErrorEmail] = useState('')
  const [accountInfoData, setAccountInfoData] = useState<AccountInfoData>(() => {
    const storedAccInfo = getItem('accountInfo')
    return storedAccInfo ?? null
  });
  const { control, handleSubmit, setValue, watch, formState: { errors }, trigger } = useForm({
    defaultValues: {
      admName: accountInfoData ? accountInfoData.admDetail?.admName : '',
      emailName: accountInfoData ? accountInfoData.admDetail?.admEmail.split('@')[0] : '',
      emailDomain: accountInfoData ? accountInfoData.admDetail?.admEmail.split('@')[1] : '',
      customDomain: accountInfoData ? accountInfoData.admDetail?.admEmail.split('@')[1] : '',
      newDomain: accountInfoData ? accountInfoData.admDetail?.admEmail.split('@')[1] : ''
    },
    resolver: yupResolver(InformationQuerySchema),
  });

  const domainList = [
    { label: '직접입력', value: '' },
    { label: 'G메일', value: 'gmail.com' },
    { label: '네이버', value: 'naver.com' },
    { label: '엠팔', value: 'empal.com' },
    { label: '네이트', value: 'nate.com' },
    { label: '다음', value: 'hanmail.net' },
    { label: '카카오', value: 'kakao.com' },
    { label: '마크애니', value: 'markany.com' },
  ];

  const getAdminAccountInfo = async () => {
    const { response, statusCode } = await accountInfoDetailService();

    if (response?.code === '1' && is200xRange(statusCode)) {
      setAccountInfoData(response?.result);
      setValue('admName', response?.result?.admDetail?.admName);
      setValue(
        'emailDomain',
        response?.result?.admDetail?.admEmail.split('@')[1],
      );
      setValue(
        'emailName',
        response?.result?.admDetail?.admEmail.split('@')[0],
      );
      setValue(
        'customDomain',
        response?.result?.admDetail?.admEmail.split('@')[1],
      );
    }
  };

  const updateAdminAccountInfo = async (
    admName: string,
    emailName: string,
    emailDomain: string,
  ) => {
    const { response, statusCode } = await accountInfoUpdateService({
      admName,
      emailName,
      emailDomain,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      removeItem('accountInfo')
      navigate(`${ROUTES.CUSTOMER_DELIVERY_FILES.ROOT}?refetch=true`);
    }
  };

  const submit = async (data: any) => {
    const { admName, emailName, customDomain } = data;
    if (isAdmin) {
      setLoading(true)
      await updateAdminAccountInfo(admName, emailName, customDomain);
      setLoading(false)
    }
  };

  const keyword = watch('customDomain');

  useEffect(() => {
    if (!accountInfoData) {
      getAdminAccountInfo();
    }
  }, [getAdminAccountInfo, accountInfoData]);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.ACCOUNT_INFO.INFO_INQUIRY.path}>
            계정정보
          </RouteLink>
          <RouteLink to={ROUTES.ACCOUNT_INFO.INFO_INQUIRY.path} isActive>
            정보조회
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          정보 조회
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(submit)}>
        <Box display="flex" flexDirection="column" gap={6} marginBottom="50px">
          {isAdmin && (
            <FieldBox>
              {/* ADMINISTRATOR ID */}
              <FieldBoxProperty
                name={isEditInfo ? '* 관리자 아이디' : '관리자 아이디'}
                type="text"
                value={accountInfoData?.admDetail?.admId ?? '--'}
              />
              {/* ADMINISTRATOR NAME */}
              <FieldBoxProperty
                name={isEditInfo ? '* 관리자 이름' : '관리자 이름'}
                type={isEditInfo ? '' : 'text'}
                value={
                  isEditInfo ? (
                    <Controller
                      name="admName"
                      control={control}
                      render={({ field }) => {
                        return (
                          <InputContainer type="form">
                            <Input
                              {...field}
                              placeholder="검색어를 입력해 주세요."
                              defaultValue={field?.value}
                              showErrorMsg={!!errors.admName}
                              errorMessage={errors.admName && errors.admName.message}
                            />
                          </InputContainer>
                        );
                      }}
                    />
                  ) : (
                    accountInfoData?.admDetail?.admName ?? '--'
                  )
                }
              />
              {/* EMAIL */}
              <FieldBoxProperty
                name={isEditInfo ? '* 이메일' : '이메일'}
                type={isEditInfo ? '' : 'text'}
                value={
                  isEditInfo ? (
                    <Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Controller
                          name="emailName"
                          control={control}
                          render={({ field }) => {
                            return (
                              <Input
                                {...field}
                                sx={{
                                  minWidth: '260px',
                                  maxWidth: '260px',
                                }}
                                placeholder="검색어를 입력해 주세요."
                                defaultValue={field?.value ?? ''}
                              />
                            );
                          }}
                        />

                        <Typography variant="body1" color="var(--base-black)">
                          @
                        </Typography>
                        <Controller
                          name="customDomain"
                          control={control}
                          render={({ field }) => {
                            return (
                              <Input
                                {...field}
                                keyword={keyword}
                                clearAction={() => {
                                  setValue(
                                    'customDomain',
                                    '',
                                  );
                                }}
                                sx={{
                                  minWidth: '260px',
                                  maxWidth: '260px',
                                }}
                                placeholder="검색어를 입력해 주세요."
                                defaultValue={field?.value ?? ''}
                                onChange={(e) => {
                                  setErrorEmail('')
                                  const val = e.currentTarget.value
                                  const domain = domainList.find((domain) => domain.value === val)
                                  field.onChange(val)
                                  if (!domain) {
                                    setValue('newDomain', val)
                                  } else {
                                    setValue('emailDomain', domain.value)
                                  }
                                }}
                              //   onBlur={(e) => {
                              //     if (e.target.value === '') {
                              //       setValue(
                              //         'customDomain',
                              //         accountInfoData?.admDetail?.admEmail?.split(
                              //           '@',
                              //         )[1] || '',
                              //       );
                              //     } else {
                              //       const isExist = domainList.findIndex(
                              //         (item) => {
                              //           return item.value === e.target.value;
                              //         },
                              //       );
                              //       if (isExist === -1) {
                              //         setValue('emailDomain', '');
                              //       } else {
                              //         setValue('emailDomain', e.target.value);
                              //       }
                              //     }
                              //   }}
                              />
                            );
                          }}
                        />

                        <Controller
                          name="emailDomain"
                          control={control}
                          render={({ field }) => {
                            return (
                              <Autocomplete
                                {...field}
                                options={domainList}
                                value={
                                  domainList.find((domain) => (
                                    domain.value === field?.value
                                  ))?.value ?? ''
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.value === value.value
                                }
                                inputStyle={{ width: '200px' }}
                                onChange={(_, newValue, reason) => {
                                  setErrorEmail('')
                                  const newDomain = watch('newDomain')
                                  field.onChange(newValue?.value);
                                  // @ts-ignore
                                  if (newValue?.label === '직접입력') {
                                    if (domainList.find((domain) => domain.value === newDomain)) {
                                      setValue('customDomain', '');
                                    }
                                    else {
                                      setValue('customDomain', newDomain);
                                    }
                                  }
                                  else {
                                    setValue('customDomain', newValue?.value as string);
                                    if (reason === 'clear') {
                                      setValue(
                                        'emailDomain',
                                        ''
                                      );
                                      setValue(
                                        'customDomain',
                                        newDomain ??
                                        (accountInfoData?.admDetail?.admEmail?.split(
                                          '@',
                                        )[1] || '')
                                      );
                                    }
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      </Box>
                      {errorEmail && (
                        <Typography sx={{ marginTop: '7px' }} color={'var(--danger-500)'}>
                          {errorEmail}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    accountInfoData?.admDetail?.admEmail ?? '--'
                  )
                }
              />

              {/* ADMINISTRATOR STATUS */}
              <FieldBoxProperty
                name="관리자 상태"
                type="text"
                value={
                  (accountInfoData?.admDetail?.admSt === '10' && '신청') ||
                  (accountInfoData?.admDetail?.admSt === '20' && '심사중') ||
                  (accountInfoData?.admDetail?.admSt === '30' && '반려') ||
                  (accountInfoData?.admDetail?.admSt === '50' && '정상')
                }
              />
            </FieldBox>
          )}

          {isStaff && (
            <FieldBox>
              {/* ID */}
              <FieldBoxProperty
                name="아이디"
                type="text"
                value={accountInfoData?.staffLoginInfo?.userUid}
              />
              {/* NAME */}
              <FieldBoxProperty
                name="성명"
                type="text"
                value={accountInfoData?.staffLoginInfo?.userName}
              />
              {/* EMAIL */}
              <FieldBoxProperty
                name="이메일"
                type="text"
                value={accountInfoData?.staffLoginInfo?.userEmail}
              />
              {/* SITUATION */}
              <FieldBoxProperty
                name="상태"
                type="text"
                value={
                  (accountInfoData?.staffLoginInfo?.userSt === '10' &&
                    '신청') ||
                  (accountInfoData?.staffLoginInfo?.userSt === '50' &&
                    '정상') ||
                  (accountInfoData?.staffLoginInfo?.userSt === '80' &&
                    '일시정지') ||
                  (accountInfoData?.staffLoginInfo?.userSt === '90' && '퇴사')
                }
              />
            </FieldBox>
          )}
        </Box>
        {isAdmin && (
          <Box>
            {isEditInfo ? (
              <Box display="flex" gap="14px">
                {/* CANCEL */}
                <Button
                  disabled={isLoading}
                  variant="outlined"
                  color="secondary"
                  sx={{ minWidth: '112px' }}
                  onClick={() => {
                    setIsEditInfo(false);
                  }}
                >
                  취소
                </Button>
                {/* SAVE */}
                <Button
                  disabled={isLoading}
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: '112px' }}
                  type="button"
                  onClick={async () => {
                    const fullEmail = `${watch('emailName')}@${watch('customDomain')}`
                    trigger()
                    setErrorEmail('')
                    if (!validateEmail(fullEmail)) {
                      return setErrorEmail('이메일이 올바르지 않습니다')
                    }
                    handleSubmit(submit)()
                  }}
                >
                  {isLoading ?
                    <CircularProgress size={18} color='secondary' />
                    :
                    ' 저장하기'
                  }
                </Button>
              </Box>
            ) : (
              <Box display="flex">
                {/* EDIT */}
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: '112px' }}
                  onClick={() => {
                    setIsEditInfo(true);
                  }}
                >
                  수정하기
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InformationInquiry;
